<template>
    <div class="text-center">
      <v-dialog
        v-model="modalStatus"
        max-width="550"
        scrollable
        persistent
      >
        <v-card>
          <v-card-text class="pa-0">
            <div class="py-4 px-8">
              <!-- Título del modal -->
              <div class="d-flex flex-row align-center justify-space-between mb-2">
                <p class="mb-0 font-weight-bold text-primary title">Editar Plano</p>
              </div>

              <!-- Campo Título -->
              <v-row align="center" class="my-2">
                <v-col cols="12">
                  <v-text-field
                    v-model="editedPlano.title"
                    label="Título"
                    placeholder="Ingrese nombre del plano"
                    outlined
                    required
                    hide-details
                    :rules="requiredRule"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Toggle Local / Forge -->
              <v-row class="mb-1">
                <v-col cols="12">
                  <p class="mb-1 caption grey--text text--darken-1">Tipo de archivo</p>
                  <v-btn-toggle v-model="fileMode" mandatory color="#2D4B95" rounded dense>
                    <v-btn value="local" small class="px-4">
                      <v-icon small class="mr-1">mdi-upload</v-icon>
                      Archivo Local
                    </v-btn>
                    <v-btn value="forge" small class="px-4">
                      <v-icon small class="mr-1">mdi-cube-scan</v-icon>
                      Archivo Forge
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Modo Local -->
              <v-row v-if="fileMode === 'local'">
                <v-col cols="12">
                  <div class="d-flex flex-column">
                    <!-- Preview nueva imagen -->
                    <img v-if="previewImage" :src="previewImage" class="preview-img mb-2"/>
                    <!-- Nombre archivo actual (si no hay preview) -->
                    <p v-if="!previewImage && currentFileName && !imageChanged" class="mb-1 caption text-primary">
                      <v-icon small color="#2D4B95" class="mr-1">mdi-file</v-icon>
                      Archivo actual: {{ currentFileName }}
                    </p>
                    <v-btn
                      class="mx-0 my-1 align-self-start"
                      rounded
                      color="white"
                      raised
                      @click="onButtonClick"
                    >
                      <v-icon small class="mr-1" color="#2D4B95">mdi-paperclip</v-icon>
                      <p class="mb-0 text-primary">{{ imageChanged ? 'Cambiar Archivo' : 'Actualizar Archivo' }}</p>
                    </v-btn>
                    <v-btn
                      v-if="previewImage"
                      x-small
                      text
                      color="red"
                      class="mx-0 mt-1 align-self-start"
                      @click="clearLocalFile"
                    >
                      <v-icon x-small class="mr-1">mdi-close</v-icon>Quitar archivo
                    </v-btn>
                    <input
                      ref="uploader"
                      class="d-none"
                      type="file"
                      @change="onFileChanged"
                    >
                  </div>
                </v-col>
              </v-row>

              <!-- Modo Forge -->
              <v-row v-if="fileMode === 'forge'">
                <v-col cols="12">
                  <v-autocomplete
                    v-model="selectedForgeFile"
                    :loading="$store.forgeStore.state.loadingFiles"
                    :items="$store.forgeStore.state.forgeFiles"
                    :item-disabled="isFileDisabled"
                    item-text="name"
                    item-value="id"
                    return-object
                    outlined
                    dense
                    label="Seleccionar archivo de Forge"
                    hide-details
                    placeholder="Buscar archivo..."
                    clearable
                  >
                    <template v-slot:item="{ item, attrs, on }">
                      <v-list-item v-bind="attrs" v-on="on">
                        <v-list-item-avatar size="32" :color="isFileDisabled(item) ? 'grey lighten-3' : 'blue lighten-5'" rounded>
                          <v-icon :color="isFileDisabled(item) ? 'grey lighten-1' : 'blue darken-1'" size="18">
                            {{ isFileProcessing(item) ? 'mdi-progress-clock' : 'mdi-file-cad' }}
                          </v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title :class="isFileDisabled(item) ? 'grey--text text--lighten-1' : ''" class="font-weight-medium">
                            {{ item.name }}
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="isFileProcessing(item)" class="d-flex align-center mt-1">
                            <v-chip x-small color="orange lighten-4" text-color="orange darken-3" class="mr-1">
                              <v-icon x-small left>mdi-clock-outline</v-icon>Procesando
                            </v-chip>
                            <span class="caption grey--text">Intenta en unos minutos</span>
                          </v-list-item-subtitle>
                          <v-list-item-subtitle v-else-if="!isFileDisabled(item)" class="d-flex align-center mt-1">
                            <v-chip x-small color="green lighten-4" text-color="green darken-3">
                              <v-icon x-small left>mdi-check-circle</v-icon>Listo para usar
                            </v-chip>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="grey--text text-center">
                            <v-icon class="mr-1">mdi-file-search-outline</v-icon>No se encontraron archivos
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <!-- Acciones -->
              <v-row align="center" class="mt-2">
                <v-col cols="12">
                  <div class="d-flex flex-row justify-space-between">
                    <v-btn class="mx-2 my-1" rounded color="white" raised @click="closeModal()">
                      <p class="mb-0 text-primary">Cancelar</p>
                    </v-btn>
                    <v-btn class="mx-2 my-1" rounded color="#2D4B95" raised dark @click="updatePlano()">
                      <v-progress-circular v-if="loading" indeterminate color="white" :width="2" :size="15" class="mr-2"></v-progress-circular>
                      <p class="mb-0">Guardar</p>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>

            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import { v4 as uuidv4 } from 'uuid'
  import { updatePlano as _updatePlano } from '@/helpers/api/entrega'

  export default {
    name: 'EditPlanoModal',
    props: {
      closeModal: {
        type: Function,
        required: true
      },
      modalStatus: {
        type: Boolean,
        default: false
      },
      planoId: {
        type: [Number, String],
        default: null
      }
    },
    data() {
      return {
        editedPlano: {
          id: null,
          title: null,
        },
        fileMode: 'local',
        selectedForgeFile: null,
        currentFileName: '',
        previewImage: null,
        files: [],
        imageChanged: false,
        loading: false,
        requiredRule: [v => !!v || 'Este campo es requerido'],
      }
    },
    watch: {
      modalStatus(val) {
        if (val) {
          this.loadPlanoData();
          this.loadForgeFiles();
        } else {
          this.resetForm();
        }
      },
      fileMode() {
        this.clearLocalFile();
        this.selectedForgeFile = null;
      }
    },
    mounted() {
      if (this.modalStatus) {
        this.loadPlanoData();
        this.loadForgeFiles();
      }
    },
    methods: {
      loadForgeFiles() {
        const projectId = this.$store.projectsStore.state.selectedProject?.id;
        if (projectId) {
          this.$store.forgeStore.dispatch('fetchForgeFiles', { project_id: projectId });
        }
      },
      resetForm() {
        this.editedPlano = { id: null, title: null };
        this.fileMode = 'local';
        this.selectedForgeFile = null;
        this.previewImage = null;
        this.files = [];
        this.imageChanged = false;
        this.currentFileName = '';
      },
      async loadPlanoData() {
        try {
          const planoId = this.planoId || this.$store.entregaStore.state.selectedPlanoId;
          const planoData = this.$store.entregaStore.state.planos.data?.find(p => p.id === planoId);

          if (planoData) {
            this.editedPlano = {
              id: planoId,
              title: planoData.title,
              project_id: this.$store.projectsStore.state.selectedProject.id
            };

            // Detectar modo según tipo de archivo
            if (planoData.file && planoData.file.forge_file && planoData.file.forge_file.urn) {
              this.fileMode = 'forge';
              // Pre-seleccionar el archivo forge actual una vez que se cargue la lista
              this.$watch('$store.forgeStore.state.forgeFiles', (files) => {
                if (files && files.length > 0) {
                  const match = files.find(f => f.id === planoData.file_id);
                  if (match) this.selectedForgeFile = match;
                }
              }, { immediate: true });
            } else {
              this.fileMode = 'local';
              if (planoData.file) {
                this.currentFileName = planoData.file.name || planoData.file.original_name || 'Archivo adjunto';
              }
            }
          }
        } catch (error) {
          console.error('Error loading plano data:', error);
          this.showMessage({ message: 'Error al cargar los datos del plano', title: 'Error', type: 'error' });
        }
      },
      isFileProcessing(file) {
        const forgeFile = file.forge_file;
        return !forgeFile || (!forgeFile.urn && !forgeFile.translate);
      },
      isFileDisabled(file) {
        return this.isFileProcessing(file);
      },
      clearLocalFile() {
        this.previewImage = null;
        this.files = [];
        this.imageChanged = false;
        if (this.$refs.uploader) {
          this.$refs.uploader.value = '';
        }
      },
      toBase64(file) {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
        });
      },
      onButtonClick() {
        this.$refs.uploader.click();
      },
      async onFileChanged(e) {
        this.imageChanged = true;
        const files = e.target.files || e.dataTransfer.files;
        if (!files.length) return;
        this.files = Array.from(files);
        const file = files[0];
        if (file.type.includes('image')) {
          this.previewImage = await this.toBase64(file);
        } else {
          this.previewImage = null;
        }
      },
      async updatePlano() {
        if (!this.editedPlano.title) {
          this.showMessage({ message: 'Por favor escribe un título. Este campo no puede estar vacío.', title: 'Error', type: 'error' });
          return;
        }

        this.loading = true;

        try {
          const formData = new FormData();
          formData.append('id', this.editedPlano.id);
          formData.append('title', this.editedPlano.title);
          formData.append('project_id', this.editedPlano.project_id);

          if (this.fileMode === 'forge') {
            if (!this.selectedForgeFile) {
              this.showMessage({ message: 'Por favor selecciona un archivo de Forge.', title: 'Error', type: 'error' });
              this.loading = false;
              return;
            }
            formData.append('file_id', this.selectedForgeFile.id);
          } else if (this.fileMode === 'local' && this.imageChanged && this.files.length > 0) {
            const biggerFiles = [];
            this.files.forEach((file) => {
              if (file && file.size < 100000) {
                formData.append('files[]', file);
              } else if (file) {
                biggerFiles.push({
                  type: 9,
                  payload: null,
                  description: this.editedPlano.title,
                  total: file.size,
                  uuid: uuidv4(),
                  file,
                  item_id: this.editedPlano.id,
                });
              }
            });

            const result = await _updatePlano(formData);
            if (result.code === 200) {
              if (biggerFiles.length > 0) {
                biggerFiles.forEach(f => {
                  f.payload = { plano_id: this.editedPlano.id, project_id: this.editedPlano.project_id };
                  this.$store.globalStore.commit('setFilesToUploadCenter', f);
                });
              }
              this.showMessage({ message: 'Plano actualizado exitosamente', title: 'Éxito', type: 'success' });
              this.closeModal();
              this.$store.entregaStore.dispatch('getPlanos', { project_id: this.editedPlano.project_id });
            } else {
              this.showMessage({ message: 'Error al actualizar el plano', title: 'Error', type: 'error' });
            }
            return;
          }

          // Para forge o local sin cambio de archivo
          const result = await _updatePlano(formData);
          if (result.code === 200) {
            this.showMessage({ message: 'Plano actualizado exitosamente', title: 'Éxito', type: 'success' });
            this.closeModal();
            this.$store.entregaStore.dispatch('getPlanos', { project_id: this.editedPlano.project_id });
          } else {
            this.showMessage({ message: 'Error al actualizar el plano', title: 'Error', type: 'error' });
          }
        } catch (error) {
          console.error('Error updating plano:', error);
          this.showMessage({ message: 'Error al actualizar el plano', title: 'Error', type: 'error' });
        } finally {
          this.loading = false;
        }
      },
      showMessage(data) {
        this.$store.globalStore.dispatch('addMessage', {
          message: data.message,
          title: data.title || 'Notificación',
          created: new Date(),
          type: data.type || 'info',
          icon: data.type === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle',
          color: data.type === 'error' ? '#fb8c00' : '#4CAF50'
        });
      }
    }
  };
  </script>
  
  <style scoped>
  .preview-img {
    max-width: 100%;
    max-height: 200px;
    margin: 10px auto;
    display: block;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .text-primary {
    color: #2D4B95 !important;
  }
  
  .v-dialog {
    border-radius: 8px;
    overflow: hidden;
  }
  
  .file-info {
    display: flex;
    align-items: center;
    padding: 8px;
    border: 1px dashed #ccc;
    border-radius: 4px;
    margin-bottom: 8px;
  }
  
  .file-info .v-icon {
    margin-right: 8px;
    color: #2D4B95;
  }
  </style>
